import React from 'react';
import PropTypes from 'prop-types';
import { injectTestId } from '@cvent/nucleus-test-automation';
import useTimezone from '@hooks/useTimezone';
import Link from 'next/link';
import { useTranslate } from 'nucleus-text';
import useMeQuery from '@hooks/useMeQuery';
import replaceDynamicUrl from '@tools/replaceDynamicUrl';
import { useAttendeeHubConfig } from '@hooks/useAttendeeHubConfig';
import useAppFeatures from '@hooks/useAppFeatures';
import { formatRelativeDate } from './notificationFlyout/formatRelativeDate';
import { getNotificationCustomPageUrl } from './getNotificationCustomPageUrl';
import { NotificationLinkedContentPageType, pageKeySlugs } from './utils';

/**
 * component that creates internal Link or a based on linked content type
 */
const NotificationLinkedContent = ({
  // POST CARINA V1 MIGRATION
  // eslint-disable-next-line react/prop-types
  children,
  toastRef,
  testID,
  styles,
  notification,
  onClick = () => null,
  eventId,
  surveyUrl,
  ariaLabelledBy
}) => {
  const { linkedContent: { content, contentType } = {} } = notification;
  const { appointmentTypeFilter } = useAppFeatures();
  const { translate } = useTranslate();
  const { customPageSettings } = useAttendeeHubConfig();
  const { data: meData } = useMeQuery();
  const attendeeInfo = {
    id: meData?.me?.id,
    firstName: meData?.me?.firstName,
    lastName: meData?.me?.lastName,
    email: meData?.me?.email
  };
  const { date } = useTimezone();
  const timestamp = formatRelativeDate(
    notification.publishedAt,
    new Date(),
    date,
    translate,
    true
  );

  // POST CARINA V1 MIGRATION
  // eslint-disable-next-line react/prop-types
  const isRead = !notification.isRead
    ? translate('Unread-Notifications-Aria')
    : '';
  let linkedNotificationAriaLabel = `${isRead} ${notification.body} ${translate(
    'Universal-Link'
  )} `;

  if (contentType === 'WebUrl') {
    let linkedContentLabel = null;
    if (content.name && content.name !== null) {
      linkedContentLabel = content.name;
    } else {
      const domain = new URL(content.href);
      linkedContentLabel = domain.hostname.replace('www.', '');
    }
    linkedNotificationAriaLabel = `${linkedNotificationAriaLabel}
    ${linkedContentLabel} ${timestamp}`;
    return (
      <div ref={toastRef}>
        <a
          href={content.href}
          target="_blank"
          rel="noopener noreferrer"
          css={styles}
          onClick={onClick}
          {...injectTestId(`${testID}-container`)}
          aria-label={linkedNotificationAriaLabel}
        >
          {children}
        </a>
      </div>
    );
  }
  if (contentType === 'NoLink') {
    return (
      <div
        role="region"
        onClick={onClick}
        css={styles}
        ref={toastRef}
        onKeyDown={() => {}}
      >
        {children}
      </div>
    );
  }
  if (contentType === 'LinkedPage' || contentType === 'LinkedExternalPage') {
    const { type, id } = content;
    let href;
    let target = '_self';
    linkedNotificationAriaLabel = `${linkedNotificationAriaLabel}${type} ${timestamp}`;
    switch (type) {
      case NotificationLinkedContentPageType.EXHIBITORS:
        href = `/events/${eventId}/exhibitors/${id}`;
        break;
      case NotificationLinkedContentPageType.SESSIONS:
        href = `/events/${eventId}/sessions/${id}`;
        break;
      case NotificationLinkedContentPageType.SCHEDULE:
        if (id) {
          href = `/events/${eventId}/schedule?apptId=${id}`;
        } else {
          href = `/events/${eventId}/schedule`;
        }
        break;
      case NotificationLinkedContentPageType.CUSTOM_PAGES:
        href = `/events/${eventId}/pages/${id}`;
        break;
      case NotificationLinkedContentPageType.ATTENDEE_LIST:
        href = `/events/${eventId}/attendees${
          appointmentTypeFilter ? '?applyTypeFilter=true' : ''
        }`;
        break;
      case NotificationLinkedContentPageType.DISCUSSIONS:
        href = `/events/${eventId}/discussions/${id}/details`;
        break;
      case NotificationLinkedContentPageType.SPEAKERS:
        href = `/events/${eventId}/speakers/${id}`;
        break;
      case NotificationLinkedContentPageType.FLOOR_PLAN:
        href = `/events/${eventId}/floorplans/${id}`;
        break;
      case NotificationLinkedContentPageType.EXTERNAL_PAGES:
      case NotificationLinkedContentPageType.DOCUMENT_PAGES:
        href = replaceDynamicUrl(
          getNotificationCustomPageUrl(type, id, customPageSettings, eventId),
          attendeeInfo
        );
        target = href.endsWith('error') ? '_self' : '_blank';
        break;
      case NotificationLinkedContentPageType.SURVEYS:
        href = surveyUrl || `/events/${eventId}/error`;
        target = surveyUrl ? '_blank' : '_self';
        break;
      default:
        href = `/events/${eventId}/${pageKeySlugs.get(type)}`;
        break;
    }
    return (
      <div
        ref={toastRef}
        role="button"
        onClick={onClick}
        onKeyDown={() => {}}
        {...injectTestId(`${testID}-container`)}
      >
        <Link
          href={href}
          passHref
          css={styles}
          aria-label={linkedNotificationAriaLabel}
          target={target}
          rel={
            type === NotificationLinkedContentPageType.EXTERNAL_PAGES ||
            type === NotificationLinkedContentPageType.DOCUMENT_PAGES
              ? 'noopener noreferrer'
              : null
          }
        >
          {children}
        </Link>
      </div>
    );
  }
  if (contentType === 'LinkedCallback') {
    return (
      <div
        ref={toastRef}
        css={styles}
        onClick={onClick}
        onKeyDown={() => {}}
        {...injectTestId(`${testID}-container`)}
        role="region"
        aria-labelledby={ariaLabelledBy}
      >
        {children}
      </div>
    );
  }
  return null;
};

NotificationLinkedContent.propTypes = {
  children: PropTypes.node,
  testID: PropTypes.string.isRequired,
  toastRef: PropTypes.any,
  styles: PropTypes.array,
  onClick: PropTypes.func,
  /** Notification object */
  notification: PropTypes.shape({
    id: PropTypes.string.isRequired,
    body: PropTypes.string.isRequired,
    icon: PropTypes.string,
    linkedContent: PropTypes.shape({
      contentType: PropTypes.string.isRequired,
      content: PropTypes.object.isRequired
    }),
    publishedAt: PropTypes.instanceOf(Date),
    isRead: PropTypes.bool
  }).isRequired,
  eventId: PropTypes.string,
  surveyUrl: PropTypes.string,
  ariaLabelledBy: PropTypes.string
};

export default NotificationLinkedContent;
